import { z } from 'zod';
import { UsageLicenseClassSchema } from './usage.ts';

/**
 * Admin statistics (F7 AC-2.1, AC-2.2): query and response shapes of `GET /api/admin/stats/*`, and the
 * part of `attempts.details` the statistics read.
 */

/**
 * What the statistics read from `attempts.details` (JSON, ≤ 2 KB, codes and ids only): theory
 * `wrongQuestionIds` and sim `penaltyCodes`. The schema lives with the attempt contract (attempt.ts,
 * F6 T-2.1), the single source; re-exported here for the statistics code that imports it from this file.
 */
export { AttemptDetailsSchema } from './attempt.ts';
export type { AttemptDetails } from './attempt.ts';

/** Dashboard ranges in days (the last N Vietnam calendar days, today included). */
export const STATS_RANGES = [7, 30, 90] as const;
export type StatsRange = (typeof STATS_RANGES)[number];
export const STATS_DEFAULT_RANGE: StatsRange = 30;

const StatsRangeValueSchema = z.union([z.literal(7), z.literal(30), z.literal(90)]);

const RANGE_BY_TEXT = { '7': 7, '30': 30, '90': 90 } as const;

/** `?range=7|30|90` (default 30). */
export const StatsRangeSchema = z
  .enum(['7', '30', '90'])
  .default('30')
  .transform((text) => RANGE_BY_TEXT[text]);

export const STATS_TOP_DEFAULT = 20;
export const STATS_TOP_MAX = 100;

export const StatsOverviewQuerySchema = z.object({ range: StatsRangeSchema });
export type StatsOverviewQuery = z.infer<typeof StatsOverviewQuerySchema>;

/** `?range=…&limit=1..100` (default 20) of the two top lists. */
export const StatsTopQuerySchema = z.object({
  range: StatsRangeSchema,
  limit: z.coerce.number().int().min(1).max(STATS_TOP_MAX).default(STATS_TOP_DEFAULT),
});
export type StatsTopQuery = z.infer<typeof StatsTopQuerySchema>;

const ExamKindSchema = z.enum(['theory', 'sim']);
const DateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const CountSchema = z.number().int().nonnegative();

export const PassRateRowSchema = z.strictObject({
  licenseClass: UsageLicenseClassSchema,
  kind: ExamKindSchema,
  /** Finished exams of signed-in users in the range. */
  attempts: CountSchema,
  passed: CountSchema,
  /** `passed / attempts`, 0..1 (rows exist only when `attempts > 0`). */
  rate: z.number().min(0).max(1),
});
export type PassRateRow = z.infer<typeof PassRateRowSchema>;

export const StatsSeriesPointSchema = z.strictObject({
  date: DateSchema,
  newUsers: CountSchema,
  /** Distinct devices active that day (`daily_active`). */
  dau: CountSchema,
  examsTheory: CountSchema,
  examsSim: CountSchema,
});
export type StatsSeriesPoint = z.infer<typeof StatsSeriesPointSchema>;

/**
 * `GET /api/admin/stats/overview`.
 *
 *  - `newUsers`: users created in the range. `dau`: devices active today (Vietnam day). `wau`: distinct
 *    devices over the last 7 days, today included (not the sum of daily counts).
 *  - `examsTheory` / `examsSim`: finished exams counted by `/api/track` (`usage_daily`, anonymous and
 *    signed-in alike); when no such event exists for a kind in the range (tracking not wired yet), the
 *    count of signed-in `attempts` of that kind stands in. `series` always comes from `usage_daily`.
 *  - `passRateByClass`: from `attempts` (signed-in users only), sorted by kind then class.
 *  - `series`: one point per day, oldest first, exactly `range` points (zeros for empty days).
 */
export const StatsOverviewSchema = z.strictObject({
  range: StatsRangeValueSchema,
  from: DateSchema,
  to: DateSchema,
  newUsers: CountSchema,
  dau: CountSchema,
  wau: CountSchema,
  examsTheory: CountSchema,
  examsSim: CountSchema,
  passRateByClass: z.array(PassRateRowSchema),
  series: z.array(StatsSeriesPointSchema),
});
export type StatsOverview = z.infer<typeof StatsOverviewSchema>;

/** `GET /api/admin/stats/top-wrong-questions`: most wrongly answered theory questions, by stable id. */
export const TopWrongQuestionsSchema = z.strictObject({
  range: StatsRangeValueSchema,
  items: z.array(z.strictObject({ questionId: z.string(), count: z.number().int().positive() })),
});
export type TopWrongQuestions = z.infer<typeof TopWrongQuestionsSchema>;

/** `GET /api/admin/stats/top-sim-faults`: most frequent sim penalty rule codes. */
export const TopSimFaultsSchema = z.strictObject({
  range: StatsRangeValueSchema,
  items: z.array(z.strictObject({ code: z.string(), count: z.number().int().positive() })),
});
export type TopSimFaults = z.infer<typeof TopSimFaultsSchema>;
